<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Corporates</h1>
        <span class="page-subtitle">Manage corporate accounts and clients</span>
      </div>
      <div class="page-header__right">
        <button class="btn-export" @click="exportToCSV()">
          <i class="fa fa-download"></i> Export CSV
        </button>
        <button class="btn-primary-action" @click="$modal.show('create-modal')">
          <i class="fa fa-plus"></i> Create Corporate
        </button>
      </div>
    </div>

    <!-- ── Search ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">
        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search corporates..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>
        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Services</th>
            <th>Budget</th>
            <th>Credit</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="corporate in corporates"
              :key="corporate.id"
              class="dd-table__row"
          >
            <td>
              <div class="customer-info">
                <div class="customer-avatar">
                  {{ (corporate.user.first_name || corporate.user.email || '?')[0].toUpperCase() }}
                </div>
                <div>
                  <div class="customer-name">
                    {{ `${corporate.user.first_name} ${corporate.user.last_name}`.trim() || '—' }}
                  </div>
                  <div class="customer-id">ID: {{ corporate.user.id }}</div>
                </div>
              </div>
            </td>
            <td>
              <span class="company-name">{{ corporate.name || '—' }}</span>
            </td>
            <td class="email-cell">{{ corporate.user.email || '—' }}</td>
            <td class="text-muted">{{ corporate.user.phone_number || '—' }}</td>
            <td>
              <div class="services-wrap" v-if="corporate.services && corporate.services.length">
    <span
        v-for="(svc, i) in corporate.services.slice(0, 2)"
        :key="i"
        class="service-badge"
    >{{ svc }}</span>
                <span
                    v-if="corporate.services.length > 2"
                    class="service-more"
                    :id="`svc-more-${corporate.id}`"
                >+{{ corporate.services.length - 2 }}</span>
                <b-tooltip
                    v-if="corporate.services.length > 2"
                    :target="`svc-more-${corporate.id}`"
                    triggers="hover"
                    placement="top"
                >
                  <div class="tooltip-services">
                    <div
                        v-for="(svc, i) in corporate.services.slice(2)"
                        :key="i"
                        class="tooltip-service-item"
                    >{{ svc }}</div>
                  </div>
                </b-tooltip>
              </div>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
              <span class="budget-val" v-if="corporate.budget">{{ corporate.budget }}</span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
                <span class="credit-val" :class="corporate.user.balance > 0 ? 'credit-val--pos' : ''">
                  {{ corporate.user.balance }} AED
                </span>
            </td>
            <td @click.prevent>
              <b-dropdown variant="none" no-caret boundary="viewport" right>
                <template #button-content>
                  <div class="action-menu-btn">
                    <i class="fa fa-ellipsis-h"></i>
                  </div>
                </template>
                <b-dropdown-item @click.prevent="loginAsCorporate(corporate.user)">
                  <i class="fa fa-sign-in mr-2"></i> Login as Corporate
                </b-dropdown-item>
                <b-dropdown-item @click="$router.push(`/corporates/${corporate.id}/edit`)">
                  <i class="fa fa-pencil mr-2"></i> Edit
                </b-dropdown-item>
                <b-dropdown-item @click="$router.push(`/users/${corporate.user.id}/purchases`)">
                  <i class="fa fa-history mr-2"></i> Purchase History
                </b-dropdown-item>
                <b-dropdown-item @click="$router.push(`/users/${corporate.user.id}/wallet-history`)">
                  <i class="fa fa-wallet mr-2"></i> Credit History
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="deleteCorporate(corporate.id)" class="text-danger">
                  <i class="fa fa-trash mr-2"></i> Delete
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>

          <tr v-if="corporates && corporates.length === 0">
            <td colspan="8" class="empty-state">
              <i class="fa fa-building"></i>
              <div>No corporates found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">Page {{ page }} of {{ lastPage }}</div>
        <div class="pagination-btns">
          <button
              class="page-btn"
              :disabled="page === 1"
              @click="previousPage()"
          ><i class="fa fa-chevron-left"></i> Prev</button>
          <span class="page-current">{{ page }}</span>
          <button
              class="page-btn"
              :disabled="page === lastPage"
              @click="nextPage()"
          >Next <i class="fa fa-chevron-right"></i></button>
        </div>
      </div>
    </div>

    <!-- ── Create Corporate Modal ── -->
    <modal name="create-modal" height="auto" :scrollable="true">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Create Corporate</span>
          <button class="dd-modal__close" @click="$modal.hide('create-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">

          <!-- Contact Info -->
          <div class="modal-section-title">Contact Info</div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">First Name <span class="required">*</span></label>
              <input v-model="createForm.user.first_name" class="dd-input" placeholder="First name" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Last Name</label>
              <input v-model="createForm.user.last_name" class="dd-input" placeholder="Last name" />
            </div>
            <div class="form-field">
              <label class="filter-label">Email <span class="required">*</span></label>
              <input v-model="createForm.user.email" type="email" class="dd-input" placeholder="email@company.com" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Phone <span class="required">*</span></label>
              <input v-model="createForm.user.phone_number" class="dd-input" placeholder="+971..." required />
            </div>
          </div>

          <!-- Company Info -->
          <div class="section-divider"><span>Company Info</span></div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Company Name <span class="required">*</span></label>
              <input v-model="createForm.name" class="dd-input" placeholder="Company name" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Website URL</label>
              <input v-model="createForm.website" class="dd-input" placeholder="https://..." />
            </div>
          </div>

          <!-- Services -->
          <div class="section-divider"><span>Services Interested</span></div>
          <div class="services-checkboxes">
            <label
                v-for="svc in services"
                :key="svc"
                class="checkbox-label"
            >
              <input
                  type="checkbox"
                  class="checkbox-input"
                  :value="svc"
                  v-model="createForm.services"
              />
              <span>{{ svc }}</span>
            </label>
          </div>

          <!-- Address -->
          <div class="section-divider"><span>Address</span></div>
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Country</label>
              <country-search-box @selected="(id) => createForm.user.address.country_id = id"></country-search-box>
            </div>
            <div class="form-field">
              <label class="filter-label">City</label>
              <city-search-box :country-id="createForm.user.address.country_id" @selected="(id) => createForm.user.address.city_id = id"></city-search-box>
            </div>
            <div class="form-field form-field--full">
              <label class="filter-label">Address</label>
              <input v-model="createForm.user.address.address" class="dd-input" placeholder="Street address" />
            </div>
            <div class="form-field">
              <label class="filter-label">Postcode</label>
              <input v-model="createForm.user.address.post_code" class="dd-input" placeholder="Postcode" />
            </div>
          </div>

          <ul class="error-list" v-if="createForm.errors && createForm.errors.length">
            <li v-for="error in createForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('create-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="createCorporate">
            <i class="fa fa-building"></i> Create
          </button>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../helpers";
import CountrySearchBox from "../components/CountrySearchBox.vue";
import CitySearchBox from "../components/CitySearchBox.vue";

export default {
  name: "Corporates",
  components: {
    "country-search-box": CountrySearchBox,
    "city-search-box": CitySearchBox,
  },
  data() {
    return {
      url: "",
      corporates: [],
      inputFilters: { q: null },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      services: [
        "Rewards & Recognition", "Long Service Awards", "Motivation",
        "On Pack Promotions", "Corporate Gifts", "Team Building",
        "Sales Incentives", "Themed Incentives", "Corporate Hospitality",
        "Marketing Promotions", "Referral Schemes", "Employee Benefits", "Prizes",
      ],
      createForm: {
        user: { first_name: "", address: { address: "", post_code: "", country_id: null, city_id: null } },
        services: [],
      },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/corporates";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page, see: 1 };
      if (this.isFiltered) myData = { ...myData, ...this.filters };
      axios.get(this.url, { params: myData }).then((r) => {
        this.corporates = r.data.data;
        this.page       = r.data.meta.current_page;
        this.lastPage   = r.data.meta.last_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      const filters = {};
      if (this.inputFilters.q) filters.q = this.inputFilters.q;
      this.filters = filters;
      this.loadData();
    },

    createCorporate(event) {
      event.preventDefault();
      axios
          .post(this.url, this.createForm)
          .then(() => {
            this.createForm = { user: { first_name: "", address: { address: "", post_code: "", country_id: null, city_id: null } }, services: [] };
            alert("Corporate created successfully!");
            this.$modal.hide("create-modal");
            this.loadData();
          })
          .catch((e) => {
            this.createForm.errors = Object.values(e.response.data.errors).flat();
          });
    },

    exportToCSV() {
      axios.get(this.url + "/export").then((r) => {
        const url  = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "corporates.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    loginAsCorporate(user) {
      axios.post(`${config("BACKEND_ROOT")}/admin/users/${user.id}/login`).then((r) => {
        const { access_token: token, refresh_token: refresh, cart } = r.data;
        window.open(
            `${config("FRONTEND_ROOT")}/auth/autologin?token=${token}&refresh=${refresh}&uuid=${cart.uuid}&count=${cart.items.length}&to=/dashboard`,
            "_blank"
        ).focus();
      });
    },

    nextPage()     { if (this.page < this.lastPage) { this.page++; this.loadData(); } },
    previousPage() { if (this.page > 1) { this.page--; this.loadData(); } },

    deleteCorporate(id) {
      if (!confirm("Do you want to delete this corporate?")) return;
      axios.delete(`${this.url}/${id}`).then(() => this.loadData());
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap   { display: flex; flex-direction: column; gap: 16px; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; }

.btn-export { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-export:hover { border-color: var(--accent); color: var(--accent); }
.btn-primary-action { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-primary-action:hover { opacity: .88; }

/* ── Filter ── */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* ── Table ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 30px; height: 30px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }
.customer-id     { font-size: 11px; color: var(--text-3); }
.company-name    { font-weight: 500; color: var(--text-1); }
.email-cell      { font-size: 12px; color: var(--text-2); }
.text-muted      { color: var(--text-3); font-size: 12px; }
.budget-val      { font-weight: 500; color: var(--text-1); }
.credit-val      { font-weight: 500; color: var(--text-2); }
.credit-val--pos { color: #22c55e; }

/* Services */
.services-wrap { display: flex; flex-wrap: wrap; gap: 4px; }
.service-badge { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 20px; font-size: 10px; font-weight: 500; background: rgba(129,140,248,.1); color: #818cf8; border: 1px solid rgba(129,140,248,.2); white-space: nowrap; }
.service-more  { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 20px; font-size: 10px; font-weight: 500; background: var(--bg-hover); color: var(--text-3); border: 1px solid var(--border); }

/* Action menu */
.action-menu-btn { width: 30px; height: 30px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-2); font-size: 14px; transition: border-color .15s, color .15s; }
.action-menu-btn:hover { border-color: var(--accent); color: var(--accent); }
.dd-table__row td:last-child .btn { padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.pagination-btns { display: flex; align-items: center; gap: 8px; }
.page-btn { height: 32px; padding: 0 12px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; color: var(--text-2); font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 5px; transition: border-color .15s, color .15s; }
.page-btn:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-current { font-size: 13px; font-weight: 500; color: var(--text-1); padding: 0 4px; }

.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* ── Modal ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

.modal-section-title { font-size: 11px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: .75rem; }
.section-divider { display: flex; align-items: center; gap: 12px; margin: 1.25rem 0 .75rem; color: var(--text-3); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; }
.section-divider::before, .section-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }

.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: span 2; }
.required { color: var(--accent); }

/* Services checkboxes */
.services-checkboxes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.checkbox-label { display: flex; align-items: center; gap: 7px; cursor: pointer; font-size: 12px; color: var(--text-2); padding: 6px 8px; border-radius: 7px; border: 1px solid var(--border); background: var(--bg-hover); transition: border-color .15s; }
.checkbox-label:hover { border-color: var(--accent); color: var(--text-1); }
.checkbox-input { accent-color: var(--accent); width: 14px; height: 14px; cursor: pointer; flex-shrink: 0; }

.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }
.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--grow { width: 100%; }
  .form-grid { grid-template-columns: 1fr; }
  .form-field--full { grid-column: span 1; }
  .services-checkboxes { grid-template-columns: 1fr 1fr; }
}
.tooltip-services { text-align: left; }
.tooltip-service-item {
  padding: 2px 0;
  font-size: 12px;
  color: #fff;
  white-space: nowrap;
}
.tooltip-service-item + .tooltip-service-item {
  border-top: 1px solid rgba(255,255,255,.1);
}
</style>